///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { AfterViewInit, ChangeDetectorRef, Directive, Input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';

import { GtthHistoryService } from '../../../project-view-panel/logical-topology/engine/history-service';
import { GtthBomReportService } from '../../../project-view-panel/project-windows/bom_report/bom-report.service';
import { validateIpBySupportedRange } from '../validation-functions/subnetwork-validation';
import { ExtendedBomDeviceDTO } from '../../../project-view-panel/project-windows/bom_report/dto/extended-bom-device-dto.model';

export const IP_PATTERN = '^(?:(?:^|\\.)(?:2(?:5[0-5]|[0-4]\\d)|1?\\d?\\d)){4}$';
export const IP_OPTIONAL_VALUE = '255.255.255.255';
export const IP_ZERO_VALUE = '0.0.0.0';
export const IP_DOES_NOT_MATCH_PATTERN_MESSAGE = 'Required format is IPv4';
export const IP_EXISTS_MESSAGE = 'IP address already exists';
export const IP_EQUALS_GW_MESSAGE = 'IP address equals to Default GW IP';
export const IP_OUT_OF_SUBNET_MESSAGE = "IP address doesn't belong to the subnetwork";
export const IP_OUT_OF_SUPPORTED_RANGE_MESSAGE = 'IP from reserved range';

export const ipAddressValidator = (
    device: ExtendedBomDeviceDTO,
    locationName: string,
    historyService: GtthHistoryService,
    bomReportService: GtthBomReportService
): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
        const ip = control.value;

        if (!new RegExp(IP_PATTERN, 'gm').test(ip)) {
            return { error: IP_DOES_NOT_MATCH_PATTERN_MESSAGE };
        }

        if (!validateIpBySupportedRange(ip)) {
            return { error: IP_OUT_OF_SUPPORTED_RANGE_MESSAGE };
        }

        if (
            ip === IP_ZERO_VALUE ||
            ip === IP_OPTIONAL_VALUE ||
            !historyService.logicalTopology.exportLinksConfig.checkIPValidityBySubnetwork(ip)
        ) {
            return { error: IP_OUT_OF_SUBNET_MESSAGE };
        }

        if (ip !== IP_ZERO_VALUE && ip !== IP_OPTIONAL_VALUE) {
            if (bomReportService.checkIpExists(locationName, device)) {
                return { warning: IP_EXISTS_MESSAGE };
            }
            if (ip === historyService.logicalTopology.exportLinksConfig.defaultGateway) {
                return { warning: IP_EQUALS_GW_MESSAGE };
            }
        }

        return null;
    };
};

@Directive({
    selector: '[windeIpAddressValidator]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: GtthIpAddressValidatorDirective,
            multi: true
        }
    ],
    standalone: false
})
export class GtthIpAddressValidatorDirective implements Validator, AfterViewInit {
    @Input() locationName: string;
    @Input() device: ExtendedBomDeviceDTO;

    constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private readonly historyService: GtthHistoryService,
        private readonly bomReportService: GtthBomReportService
    ) {}

    ngAfterViewInit() {
        this.changeDetector.detectChanges();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return ipAddressValidator(this.device, this.locationName, this.historyService, this.bomReportService)(control);
    }
}

// This directive should be used in AN and IP report where we just show values in read-only mode
@Directive({
    selector: '[windeIpAddressViewValidator]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: GtthIpAddressViewValidatorDirective,
            multi: true
        }
    ],
    standalone: false
})
export class GtthIpAddressViewValidatorDirective implements Validator {
    @Input() locationName: string;
    @Input() device: ExtendedBomDeviceDTO;

    constructor(
        private readonly historyService: GtthHistoryService,
        private readonly bomReportService: GtthBomReportService
    ) {}

    validate(control: AbstractControl): ValidationErrors | null {
        return ipAddressValidator(this.device, this.locationName, this.historyService, this.bomReportService)(control);
    }
}
